import React, { Fragment } from 'react';
import {
  FileIconPdf,
  FileIconMov,
  FileIconRvt,
  FileIconZip,
  FileIconFile,
} from 'icons';
import * as Styled from './FilesList.styles';

export interface AttachmentFile {
  id: string;
  name: string;
  allow_guest: boolean;
  created_on: string;
  description: string;
  file_size: number;
  file_type: string;
  section: string;
  updated_on: string;
  file_url: string;
  url: string;
  pim_id: string;
  document_type: string;
}

export interface FilesListProps {
  list: AttachmentFile[];
  onPointerDown?: (
    url: string,
    id?: string,
    name?: string,
    fileUrl?: string,
  ) => void;
  item?: null | Record<string, any>;
  isRedirectClickAllowed?: boolean;
  downloadButtonLabel?: string;
}

/**
 * Returns the analytics item type based on the item properties.
 * @param item - The item to analyze.
 * @returns The analytics item type.
 */
export const getAnalyticsItemType = (item: any) => {
  if (!item) {
    return null;
  }

  const productFinishTypeValue =
    item.productFinishType ?? item.product_finish_type;
  if (productFinishTypeValue?.toLowerCase().includes('product')) {
    return 'Non sampleable';
  } else if (productFinishTypeValue?.toLowerCase().includes('finish')) {
    return 'Finish';
  }

  return 'Sampleable';
};

const icons: Record<string, JSX.Element> = {
  pdf: <FileIconPdf />,
  mov: <FileIconMov />,
  rvt: <FileIconRvt />,
  zip: <FileIconZip />,
};

/**
 * Returns the appropriate file icon based on the file type.
 * @param type - The file type.
 * @returns The corresponding file icon component.
 */
const getFileIcon = (type = '') => {
  return icons[`${type?.toLowerCase()}`] || <FileIconFile />;
};

interface FileItemProps {
  item: AttachmentFile;
  onFileClick: (e: React.SyntheticEvent) => void;
  downloadButtonLabel?: string;
  handleRightClick?: (e: React.SyntheticEvent) => void;
}

/**
 * Represents a single file item in the list.
 * @param item - The file item data.
 * @param onFileClick - The function to handle file click events.
 * @param downloadButtonLabel - The label for the download button.
 */
const FileItem: React.FC<FileItemProps> = ({
  item,
  onFileClick,
  downloadButtonLabel,
  handleRightClick,
}) => (
  <Styled.FileLink key={item.id}>
    <Styled.InnerWrapper
      href={item.url}
      title={item.name}
      onClick={onFileClick}
    >
      <Styled.IconWrapper>{getFileIcon(item.file_type)}</Styled.IconWrapper>
      <Styled.FileInfo>
        <Styled.FileDescription variant="tiny">
          {item.name}
        </Styled.FileDescription>
        <Styled.FileTitle variant="tiny">{item.description}</Styled.FileTitle>
      </Styled.FileInfo>
    </Styled.InnerWrapper>
    <Styled.FileData
      onClick={onFileClick}
      onContextMenu={handleRightClick}
      target="_blank"
      href={item.file_url}
      title={item.name}
      download={`file.${item.file_type}`}
      draggable={false}
    >
      {Math.max(0.1, item.file_size / 1024 ** 2).toFixed(1)} MB ·{' '}
      <span>{downloadButtonLabel || 'Download'}</span>
    </Styled.FileData>
  </Styled.FileLink>
);

/**
 * Represents a list of files.
 * @param list - The list of file items.
 * @param onPointerDown - The function to handle pointer down events.
 * @param isRedirectClickAllowed - Whether redirect clicks are allowed.
 * @param downloadButtonLabel - The label for the download button.
 */
const FilesList: React.FC<FilesListProps> = ({
  list = [],
  onPointerDown,
  isRedirectClickAllowed = true,
  downloadButtonLabel,
}) => {
  // Handle file click events.
  const handleFileClicked = (
    e: React.SyntheticEvent,
    url: string,
    id: string,
    name: string,
    fileUrl: string,
  ) => {
    if (!isRedirectClickAllowed) {
      e.preventDefault();
    }
    onPointerDown?.(url, id, name, fileUrl);
  };

  const renderFiles = (item: AttachmentFile) => {
    // Handle file click events.
    const onFileClick = (e: React.SyntheticEvent) =>
      handleFileClicked(
        e,
        item.url,
        item.pim_id || item.id,
        item.name,
        item.file_url,
      );
    const onRightClick = (e: React.SyntheticEvent) => e.preventDefault();
    return (
      <FileItem
        key={item.id}
        item={item}
        onFileClick={onFileClick}
        downloadButtonLabel={downloadButtonLabel}
        handleRightClick={onRightClick}
      />
    );
  };

  if (list?.length === 0) return <Fragment />;

  return (
    <Styled.FilesWrapper>
      {list?.map((item: AttachmentFile) => renderFiles(item))}
    </Styled.FilesWrapper>
  );
};

export { FilesList };
